import { MonthlyReport } from '../../types/domain';
import {
  buildProgressSummary,
  formatLongDate,
  formatMonthLabel,
  formatNumber,
  formatSiteUse,
  inTargetPercent
} from '../monthlyReport';
import { getMeasurementContextLabel } from '../domain-data';

/**
 * The monthly report as a printable document, independent of any PDF library.
 *
 * Splitting the *content* of the PDF from the *drawing* of it is what makes
 * the report testable: the layout engine needs a canvas and a 300 KB library,
 * while what the report actually says is a pure function of the snapshot.
 * FR-18's exports render the same model.
 */

export type PdfBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'keyValues'; items: { label: string; value: string }[] }
  | { kind: 'bullets'; items: string[] }
  | {
      kind: 'table';
      columns: string[];
      rows: string[][];
      /**
       * Relative column widths. Omitted, the first column takes the slack and
       * the rest are sized for numbers — the monthly report's shape. FR-18's
       * exports have several text columns and pass their own.
       */
      widths?: number[];
      /** Per-column alignment; defaults to left for the first, right for the rest. */
      align?: ('left' | 'right')[];
    }
  /** A chart the page has already drawn; renderers look it up by `id`. */
  | { kind: 'chart'; id: string; title: string };

export interface PdfDocumentModel {
  /** Also the suggested file name stem. */
  title: string;
  /** Defaults to portrait. A wide export table needs landscape (FR-18). */
  orientation?: 'portrait' | 'landscape';
  subtitle: string;
  /** Header lines: who, which month, when it was generated. */
  meta: string[];
  blocks: PdfBlock[];
}

/** Chart ids the page tags its rendered SVGs with. */
export const REPORT_CHART_IDS = ['glucose', 'context', 'insulin', 'hba1c'] as const;
export type ReportChartId = (typeof REPORT_CHART_IDS)[number];

const mgdl = (value: number | null | undefined) =>
  value === null || value === undefined ? '–' : `${formatNumber(value)} mg/dL`;

/** A safe file name stem: "Laporan-Bulanan-Ayu-2026-03". */
export function reportFileName(patientName: string, periodMonth: string): string {
  const slug = (patientName || 'Pasien')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 40);
  return `Laporan-Bulanan-${slug || 'Pasien'}-${periodMonth.slice(0, 7)}`;
}

export function buildReportDocument(
  report: MonthlyReport,
  patient: { fullName: string | null; controlPlace?: string | null }
): PdfDocumentModel {
  const m = report.metrics;
  const blocks: PdfBlock[] = [];
  const inTarget = inTargetPercent(m);

  const meta = [
    `Nama: ${patient.fullName || 'Tidak tercatat'}`,
    `Periode: ${formatLongDate(m.period.start)} – ${formatLongDate(m.period.end)}`,
    `Dibuat: ${formatLongDate(report.generatedAt.slice(0, 10))}`
  ];
  if (patient.controlPlace) {
    meta.splice(1, 0, `Tempat kontrol: ${patient.controlPlace}`);
  }
  if (!m.period.complete) {
    // Say so on the document itself: a half-month report printed and filed
    // without this line is indistinguishable from a full one.
    meta.push(
      `Catatan: bulan ini masih berjalan — angka dihitung dari ${m.period.daysElapsed} hari pertama.`
    );
  }

  // --- 1. Ringkasan --------------------------------------------------------
  blocks.push({ kind: 'heading', text: 'Ringkasan' });
  blocks.push({
    kind: 'keyValues',
    items: [
      { label: 'Jumlah cek gula darah', value: String(m.glucose.readings) },
      { label: 'Rata-rata gula darah', value: mgdl(m.glucose.average) },
      {
        label: 'Di rentang aman',
        value: inTarget === null ? '–' : `${inTarget}%`
      },
      { label: 'Gula darah rendah (hipo)', value: `${m.glucose.hypoCount} kali` },
      { label: 'Gula darah tinggi (hiper)', value: `${m.glucose.hyperCount} kali` },
      { label: 'Jumlah suntikan insulin', value: String(m.insulin.injections) },
      { label: 'Total insulin', value: `${formatNumber(m.insulin.totalUnits)} unit` },
      {
        label: 'Rata-rata insulin per hari',
        value: `${formatNumber(m.insulin.averageDailyUnits)} unit`
      },
      {
        label: 'Jadwal insulin per hari',
        value:
          m.insulin.plannedDailyUnits > 0
            ? `${formatNumber(m.insulin.plannedDailyUnits)} unit`
            : 'Belum diatur'
      },
      {
        label: 'Kepatuhan pencatatan',
        value: `${m.compliance.percent}% (${m.compliance.daysWithAnyEntry}/${m.compliance.daysElapsed} hari)`
      },
      {
        label: 'Catatan lipodistrofi',
        value: `${m.sites.lipodystrophyCount} kali`
      },
      {
        label: 'Lokasi paling sering',
        value: m.sites.mostUsed.length ? formatSiteUse(m.sites.mostUsed[0]) : '–'
      }
    ]
  });

  // --- 2. Gula darah -------------------------------------------------------
  blocks.push({ kind: 'heading', text: 'Gula darah' });
  blocks.push({
    kind: 'paragraph',
    text:
      `Rentang target yang dipakai bulan ini: sebelum makan ${m.thresholds.pre.low}–` +
      `${m.thresholds.pre.normalMax} mg/dL, sesudah makan ${m.thresholds.post.low}–` +
      `${m.thresholds.post.normalMax} mg/dL.`
  });
  blocks.push({ kind: 'chart', id: 'glucose', title: 'Gula darah harian' });

  if (m.glucose.byContext.length) {
    blocks.push({
      kind: 'table',
      columns: ['Waktu pemeriksaan', 'Jumlah', 'Rata-rata', 'Terendah', 'Tertinggi'],
      rows: m.glucose.byContext.map(row => [
        getMeasurementContextLabel(
          row.context === 'tidak-tercatat' ? null : row.context
        ),
        String(row.count),
        formatNumber(row.average),
        formatNumber(row.minimum),
        formatNumber(row.maximum)
      ])
    });
    blocks.push({ kind: 'chart', id: 'context', title: 'Rata-rata per waktu pemeriksaan' });
  } else {
    blocks.push({
      kind: 'paragraph',
      text: 'Belum ada hasil cek gula darah pada bulan ini.'
    });
  }

  // --- 3. Insulin ----------------------------------------------------------
  blocks.push({ kind: 'heading', text: 'Insulin' });
  blocks.push({ kind: 'chart', id: 'insulin', title: 'Insulin harian' });
  if (m.insulin.byProduct.length) {
    blocks.push({
      kind: 'table',
      columns: ['Jenis insulin', 'Jumlah suntikan', 'Total unit'],
      rows: m.insulin.byProduct.map(row => [
        row.label,
        String(row.injections),
        formatNumber(row.units)
      ])
    });
  } else {
    blocks.push({
      kind: 'paragraph',
      text: 'Belum ada suntikan insulin yang tercatat pada bulan ini.'
    });
  }

  // --- 4. Lokasi suntik ----------------------------------------------------
  blocks.push({ kind: 'heading', text: 'Lokasi suntik' });
  blocks.push({
    kind: 'paragraph',
    text:
      `${m.sites.injections} suntikan pada ${m.sites.distinctPoints} titik berbeda. ` +
      `${m.sites.lipodystrophyCount} catatan benjolan atau penebalan kulit (lipodistrofi).`
  });
  if (m.sites.mostUsed.length) {
    blocks.push({
      kind: 'table',
      columns: ['Titik suntik', 'Jumlah pemakaian'],
      rows: m.sites.mostUsed.map(use => [formatSiteUse(use), String(use.count)])
    });
  }

  // --- 5. Data klinis ------------------------------------------------------
  blocks.push({ kind: 'heading', text: 'Data klinis' });
  blocks.push({
    kind: 'keyValues',
    items: [
      {
        label: 'Berat badan terakhir',
        value:
          m.clinical.latestWeightKg === null
            ? '–'
            : `${formatNumber(m.clinical.latestWeightKg)} kg`
      },
      {
        label: 'Tinggi badan terakhir',
        value:
          m.clinical.latestHeightCm === null
            ? '–'
            : `${formatNumber(m.clinical.latestHeightCm)} cm`
      },
      {
        label: 'HbA1c terakhir',
        value: m.clinical.hba1cTrend.length
          ? `${formatNumber(m.clinical.hba1cTrend[m.clinical.hba1cTrend.length - 1].hba1c)} %`
          : '–'
      }
    ]
  });
  if (m.clinical.hba1cTrend.length) {
    blocks.push({ kind: 'chart', id: 'hba1c', title: 'Perubahan HbA1c' });
    blocks.push({
      kind: 'table',
      columns: ['Tanggal', 'HbA1c (%)'],
      rows: m.clinical.hba1cTrend.map(point => [
        formatLongDate(point.date),
        formatNumber(point.hba1c)
      ])
    });
  } else {
    blocks.push({
      kind: 'paragraph',
      text: 'Belum ada hasil HbA1c yang tercatat.'
    });
  }

  // --- 6. Ringkasan perkembangan ------------------------------------------
  blocks.push({ kind: 'heading', text: 'Ringkasan perkembangan' });
  blocks.push({ kind: 'bullets', items: buildProgressSummary(m) });

  return {
    title: `Laporan Bulanan ${formatMonthLabel(report.periodMonth)}`,
    subtitle: 'DiaryDM — catatan mandiri diabetes melitus tipe 1',
    meta,
    blocks
  };
}
